import React, { useMemo, useRef, useEffect } from 'react';

/**
 * EmberField — tiny red "fires" + larger teardrop flames + tumbling
 * gym-equipment silhouettes floating upward in 3D behind the hero.
 * The whole field gently parallaxes with the user's mouse via a CSS
 * custom-property pipe (`--mx`, `--my`).
 *
 * Performance:
 *   • Particles are 100% CSS-animated (keyframes in index.css). Zero JS
 *     per frame.
 *   • Mouse-tracking writes two CSS vars (`--mx` / `--my`) on the
 *     wrapper only — the browser does the rest. RAF-batched + soft
 *     transition so jittery mouse motion looks smooth.
 *   • Each particle's transform animation runs on the GPU compositor
 *     via `will-change: transform, opacity` and `contain: layout style
 *     paint` — never triggers layout reflow.
 *
 * Props:
 *   count    – number of embers (small fast-moving dots)
 *   flames   – number of teardrop flame particles
 *   gym      – number of tumbling gym-equipment silhouettes
 *   reactive – when true, attaches a window pointermove listener and
 *              writes --mx / --my onto this wrapper. Defaults true.
 */
// Inline SVG icons — kept here so each particle is a single light DOM
// node. All paths are 24×24 viewBox, designed as simple solid silhouettes
// at low alpha so they read as ambient red texture, not loud focal art.
const GYM_ICONS = [
  // Dumbbell
  (
    <svg viewBox="0 0 24 24" fill="currentColor" key="dumbbell">
      <rect x="1" y="9" width="2.5" height="6" rx="0.5" />
      <rect x="3.5" y="7" width="2.5" height="10" rx="0.5" />
      <rect x="6" y="11" width="12" height="2" rx="0.7" />
      <rect x="18" y="7" width="2.5" height="10" rx="0.5" />
      <rect x="20.5" y="9" width="2.5" height="6" rx="0.5" />
    </svg>
  ),
  // Kettlebell
  (
    <svg viewBox="0 0 24 24" fill="currentColor" key="kettle">
      <path d="M9 4 L9 6 Q5 7 5 12 Q5 19 12 19 Q19 19 19 12 Q19 7 15 6 L15 4 Q15 3 14 3 L10 3 Q9 3 9 4 Z" />
    </svg>
  ),
  // Weight plate (45 lb)
  (
    <svg viewBox="0 0 24 24" fill="currentColor" key="plate">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" fill="#000" />
    </svg>
  ),
  // Barbell with plates
  (
    <svg viewBox="0 0 24 24" fill="currentColor" key="barbell">
      <rect x="2" y="11" width="20" height="2" rx="0.7" />
      <rect x="3" y="8" width="2.2" height="8" rx="0.6" />
      <rect x="5.5" y="6.5" width="2.2" height="11" rx="0.6" />
      <rect x="16.3" y="6.5" width="2.2" height="11" rx="0.6" />
      <rect x="18.8" y="8" width="2.2" height="8" rx="0.6" />
    </svg>
  ),
];

export default function EmberField({ count = 60, flames = 18, gym = 12, reactive = true, fixed = false }) {
  const wrapRef = useRef(null);

  // === Mobile/touch downscale ===
  // Coarse-pointer devices (phones, tablets) get a heavy particle cut.
  // 200 particles × CSS keyframes is fine on a modern laptop but
  // measurably costly on a 4-year-old phone. GPU-composited still, but
  // many small layers add memory & paint pressure during scroll.
  //
  // Strategy:
  //   • `fixed` (the global App-level background) → SKIP entirely on
  //     coarse pointers. On mobile the hero already has two ember
  //     layers, a third always-on global field is pure cost with no
  //     visual benefit.
  //   • Non-fixed (in-section) → 0.18× particle count on mobile.
  //   • Treat unknown `deviceMemory` (iOS Safari always reports
  //     undefined) as low-end and apply the same aggressive cut.
  //
  // === MOBILE / iPAD STATIC MODE ===
  // On coarse-pointer touch devices we no longer animate the global
  // field. Instead we render a tiny, fully-static decorative layer
  // (8 small red dots — no keyframes, no will-change, no JS, no
  // pointermove listener, no IntersectionObserver). This preserves
  // a hint of the brand's ember texture without ANY continuous GPU
  // compositing or animation loops — safe for the oldest iPhones.
  const isCoarseTouch = typeof window !== 'undefined'
    && window.matchMedia
    && (window.matchMedia('(pointer: coarse)').matches
        || window.matchMedia('(hover: none)').matches);
  // Static mode applies to EVERY EmberField instance on touch — fixed
  // and non-fixed alike. The fixed one renders 8 static dots; non-fixed
  // ones (Hero overlay layers) return null. Net mobile/iPad cost: 8 DOM
  // nodes, zero animation loops, zero RAF, zero IntersectionObserver,
  // zero pointermove listeners.
  const staticMode = isCoarseTouch;

  const { scale, skip } = useMemo(() => {
    if (typeof window === 'undefined') return { scale: 1, skip: false };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return { scale: 1, skip: true };
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const knownMem = typeof navigator !== 'undefined' && typeof navigator.deviceMemory === 'number';
    const lowMem = knownMem && navigator.deviceMemory < 4;
    if (coarse && fixed) return { scale: 1, skip: true }; // animated field skipped on touch — static mode renders below
    if (lowMem) return { scale: 0.18, skip: false };
    if (coarse) return { scale: 0.18, skip: false };
    return { scale: 1, skip: false };
  }, [fixed]);
  const emberCount = skip ? 0 : Math.round(count * scale);
  const flameCount = skip ? 0 : Math.round(flames * scale);
  const gymCount = skip ? 0 : Math.round(gym * scale);

  const embers = useMemo(() => {
    const arr = [];
    for (let i = 0; i < emberCount; i++) {
      const left = Math.random() * 100;
      const top = 40 + Math.random() * 60;
      const dx = (Math.random() - 0.5) * 90;
      const dy = -(60 + Math.random() * 180);
      const dz = (Math.random() - 0.5) * 100;
      const dur = 6 + Math.random() * 7;
      const delay = -Math.random() * 12;
      const size = 3 + Math.random() * 4;
      const peak = 0.55 + Math.random() * 0.35;
      arr.push({ i, left, top, dx, dy, dz, dur, delay, size, peak });
    }
    return arr;
  }, [emberCount]);

  const flameList = useMemo(() => {
    const arr = [];
    for (let i = 0; i < flameCount; i++) {
      const left = Math.random() * 100;
      const top = 55 + Math.random() * 50;
      const dx = (Math.random() - 0.5) * 50;
      const dy = -(90 + Math.random() * 200);
      const dz = (Math.random() - 0.5) * 60;
      const dur = 7 + Math.random() * 6;
      const delay = -Math.random() * 14;
      const w = 3 + Math.random() * 3; // 3-6 px
      const h = 6 + Math.random() * 6; // 6-12 px
      const peak = 0.65 + Math.random() * 0.3;
      arr.push({ i, left, top, dx, dy, dz, dur, delay, w, h, peak });
    }
    return arr;
  }, [flameCount]);

  const gymList = useMemo(() => {
    const arr = [];
    for (let i = 0; i < gymCount; i++) {
      // Spread across the full vertical range so equipment drifts at all depths.
      const left = Math.random() * 100;
      const top = 25 + Math.random() * 75;
      const dx = (Math.random() - 0.5) * 120;
      const dy = -(80 + Math.random() * 220);
      const dz = (Math.random() - 0.5) * 140;
      const dur = 11 + Math.random() * 9; // 11-20 s — slower than embers (heavier!)
      const delay = -Math.random() * 18;
      // 8-16 px — small enough to be ambient, big enough to read as a shape
      const size = 8 + Math.random() * 8;
      // Vary peak alpha so deeper ones look further away
      const peak = 0.32 + Math.random() * 0.4;
      const iconIdx = Math.floor(Math.random() * GYM_ICONS.length);
      arr.push({ i, left, top, dx, dy, dz, dur, delay, size, peak, iconIdx });
    }
    return arr;
  }, [gymCount]);

  // === Mouse parallax ===
  // We listen on the window (so the offset still updates even when the
  // cursor leaves a section), normalize X/Y to [-1, 1] relative to the
  // viewport, and write the values onto the wrapper as CSS vars.
  // RAF-coalesced so quick mouse motion writes at most once per frame.
  useEffect(() => {
    if (staticMode) return undefined; // touch static mode — no parallax
    if (!reactive || typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    let raf = 0;
    let mx = 0;
    let my = 0;
    const handle = (e) => {
      mx = (e.clientX / window.innerWidth) * 2 - 1;
      my = (e.clientY / window.innerHeight) * 2 - 1;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          el.style.setProperty('--mx', mx.toFixed(3));
          el.style.setProperty('--my', my.toFixed(3));
          raf = 0;
        });
      }
    };
    window.addEventListener('pointermove', handle, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handle);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reactive]);

  // === Offscreen pause ===
  // When the field scrolls out of the viewport we pause every child's
  // animation via a CSS class. This means the user pays the GPU cost of
  // ~200 particle layers ONLY while they're looking at the hero — for
  // the other 90% of the scroll path the section costs nothing.
  // (Skipped for `fixed` mode — that layer is always in the viewport.)
  useEffect(() => {
    if (staticMode) return undefined; // touch static mode — nothing to pause
    if (fixed) return undefined;
    if (typeof window === 'undefined') return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    // Already paused if user prefers reduced motion (CSS handles it).
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.remove('paused');
        else el.classList.add('paused');
      },
      { rootMargin: '50px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [fixed]);

  // === MOBILE / iPAD STATIC RENDER ===
  // 8 fixed-position red dots, no animation, no parallax, no inline
  // CSS custom properties. Tiny DOM footprint, zero compositor cost
  // beyond the initial paint. Positions are deterministic so the same
  // user sees the same pattern across reloads (avoids a Math.random
  // "redraw flicker" on bfcache restore — naturalWidth checks aren't
  // even needed because there are no <img> elements here).
  //
  // For the `fixed` global EmberField we render the 8 static dots.
  // For non-fixed instances (e.g. the two inside Hero that overlay
  // the team-stage area) we render NOTHING on touch — the global
  // static layer already provides texture, and per the production
  // safety law we cap total static particles at <= 10 sitewide.
  if (staticMode) {
    if (!fixed) return null;
    return (
      <div
        aria-hidden
        className={`ember-stage-static ${fixed ? 'fixed inset-0 z-[-1]' : 'absolute inset-0'} overflow-hidden pointer-events-none`}
      >
        <span className="ember-static" style={{ left: '8%', top: '22%' }} />
        <span className="ember-static" style={{ left: '23%', top: '68%' }} />
        <span className="ember-static" style={{ left: '37%', top: '34%' }} />
        <span className="ember-static" style={{ left: '52%', top: '80%' }} />
        <span className="ember-static" style={{ left: '64%', top: '18%' }} />
        <span className="ember-static" style={{ left: '76%', top: '55%' }} />
        <span className="ember-static" style={{ left: '88%', top: '30%' }} />
        <span className="ember-static" style={{ left: '15%', top: '88%' }} />
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      aria-hidden
      className={`ember-stage ${fixed ? 'ember-stage-fixed fixed inset-0 z-[-1]' : 'absolute inset-0'} overflow-hidden pointer-events-none`}
      style={{ perspective: '900px', transformStyle: 'preserve-3d' }}
    >
      {/* Tiny embers — dots */}
      {embers.map((p) => (
        <span
          key={`e-${p.i}`}
          className="ember"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--dz': `${p.dz}px`,
            '--dur': `${p.dur}s`,
            '--delay': `${p.delay}s`,
            '--peak': p.peak,
          }}
        />
      ))}

      {/* Tiny flames — teardrop shapes that flicker */}
      {flameList.map((p) => (
        <span
          key={`f-${p.i}`}
          className="flame"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.w}px`,
            height: `${p.h}px`,
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--dz': `${p.dz}px`,
            '--dur': `${p.dur}s`,
            '--delay': `${p.delay}s`,
            '--peak': p.peak,
          }}
        />
      ))}

      {/* Tumbling gym equipment silhouettes — dumbbells, kettlebells,
          plates, barbells. Slower than embers, with a 720° tumble so
          they feel weighty as they drift. */}
      {gymList.map((p) => (
        <span
          key={`g-${p.i}`}
          className="gym-particle"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--dz': `${p.dz}px`,
            '--dur': `${p.dur}s`,
            '--delay': `${p.delay}s`,
            '--peak': p.peak,
          }}
        >
          {GYM_ICONS[p.iconIdx]}
        </span>
      ))}
    </div>
  );
}
